import { useMemo } from 'react';
import type { PrintRunRecord } from '../types';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';

export const positiveId = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

/** Draft rendering and submissions use only the API's owned, captured COA. */
export function decodeDraftPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
  packageId: unknown,
): Record<string, any> | null {
  const id = positiveId(printRunId);
  const activeId = positiveId(packageId);
  const snapshot = run?.draftCoaSnapshot;
  const coaId = positiveId(snapshot?.packageCoaId);
  if (!id || !activeId || !coaId || (run?.status !== 4 && run?.status !== 'Draft')
    || positiveId(run.id) !== id || positiveId(run.packageId) !== activeId
    || !snapshot || positiveId(snapshot.printRunId) !== id
    || positiveId(snapshot.packageId) !== activeId || typeof snapshot.data !== 'string') return null;
  try {
    const data: unknown = JSON.parse(snapshot.data);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    return normalizeCoaKeys({ ...data, coaId });
  } catch {
    return null;
  }
}

export default function useDraftPrintCoa(
  run: PrintRunRecord | null | undefined,
  printRunId: unknown,
  packageId: unknown,
) {
  return useMemo(() => decodeDraftPrintCoa(run, printRunId, packageId), [run, printRunId, packageId]);
}
